import * as React from "react";
import { styled, alpha } from "@mui/material/styles";
import Menu, { MenuProps } from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faDownload } from "@fortawesome/free-solid-svg-icons";
import {
  faApple,
  faWindows,
  IconDefinition,
} from "@fortawesome/free-brands-svg-icons";
import { Stack, Tooltip, Typography } from "@mui/material";
import { GButton } from "../../../components/GButton";
import styles from "./css/GadgetInstallerSelectionButton.module.css";
import { downloadFile, getFileSize } from "../../../helper/utils";
import { useLoginValidator } from "../../GadgetDetails/hooks/useLoginValidator";
import { MacOSHelpGuide } from "../../../components/MacOSHelpGuide";

declare const genv: any;
const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color:
      theme.palette.mode === "light"
        ? "rgb(55, 65, 81)"
        : theme.palette.grey[300],
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      "&:active": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
  },
}));


interface InstallerMenuItemProps {
  title: string;
  icon: IconDefinition;
  packageSize: number;
  installerUrl: string;
  setAnchorEl: any;
}

const InstallerMenuItem = ({
  title,
  icon,
  packageSize,
  installerUrl,
  setAnchorEl
}: InstallerMenuItemProps) => {
  const { validateLogin } = useLoginValidator();
  const downloadPackage = () => downloadFile(installerUrl, `installer`);

  const handleClose = (event: React.MouseEvent<HTMLLIElement, MouseEvent>,) => {
    setAnchorEl(null);
    validateLogin(downloadPackage, [event]);
  };

  return (
    <Tooltip title="File size may slightly vary after download due to encoding or system differences.">
      <MenuItem onClick={handleClose} disableRipple className={styles.menuItem}>
        <FontAwesomeIcon size="1x" icon={icon} style={{ color: "#5c5c5c" }} />
        <Typography className={styles.menuItemTitle}>{title}</Typography>
        <Typography variant="inherit" className={styles.menuItemSubtitle}>
          {packageSize} MB
        </Typography>
    </MenuItem>
    </Tooltip>
  );
};

export default function GarudaInstallerSelectionButton() {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const [windowsFileSize, setWindowsFileSize] = React.useState<number>(187);
  const [macFileSize, setMacFileSize] = React.useState<number>(189);

  React.useEffect(() => {
    getFileSize(genv.DESKTOP_CLIENT_WINDOWS_INSTALLER_URL).then((size: number | undefined) => size && setWindowsFileSize(size));
    getFileSize(genv.DESKTOP_CLIENT_MAC_INSTALLER_URL).then((size: number | undefined) => size && setMacFileSize(size));
  }, []);

  return (
    <>
      <Stack spacing={2}>
        <GButton
          id="demo-customized-button"
          data-testid="get-garuda-btn"
          aria-controls={open ? "demo-customized-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          variant="contained"
          disableElevation
          color="primary"
          className={styles.installerSelectionBtn}
          size="large"
          onClick={handleClick}
          startIcon={<FontAwesomeIcon icon={faDownload} />}
          endIcon={<FontAwesomeIcon icon={faAngleDown} />}
        >
          Get Garuda
        </GButton>
        <StyledMenu
          id="demo-customized-menu"
          MenuListProps={{
            "aria-labelledby": "demo-customized-button",
          }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          sx={{ minWidth: "350px" }}
        >
          <InstallerMenuItem title="Windows" icon={faWindows} packageSize={windowsFileSize} installerUrl={genv.DESKTOP_CLIENT_WINDOWS_INSTALLER_URL} setAnchorEl={setAnchorEl}/>
          <InstallerMenuItem title="Mac" icon={faApple} packageSize={macFileSize} installerUrl={genv.DESKTOP_CLIENT_MAC_INSTALLER_URL} setAnchorEl={setAnchorEl}/>
          {/* <InstallerMenuItem title="Linux" icon={faLinux} packageSize="252 MB" installerUrl={genv.DESKTOP_CLIENT_LINUX_INSTALLER_URL} setAnchorEl={setAnchorEl}/> */}
        </StyledMenu>
        <MacOSHelpGuide />
      </Stack>
    </>
  );
}
